import { computed, reactive } from 'vue';

import { getAdminSession } from '../auth/session';
import { apiRequest } from '../http/api-client';

export interface TodoItem {
  readonly key: string;
  readonly label: string;
  readonly count: number;
  readonly route: string;
}

interface TodoState {
  items: readonly TodoItem[];
  total: number;
  loaded: boolean;
}

const POLL_INTERVAL_MS = 60_000;

const state = reactive<TodoState>({ items: [], total: 0, loaded: false });
let timer: ReturnType<typeof setInterval> | undefined;
let previousByKey = new Map<string, number>();

export const todoState = state;
export const todoTotal = computed(() => state.total);

export function todoCountForRoute(route: string): number {
  return state.items
    .filter((item) => item.route === route)
    .reduce((sum, item) => sum + item.count, 0);
}

async function refresh(): Promise<void> {
  if (!getAdminSession()) {
    stopTodoPolling();
    return;
  }
  try {
    const summary = await apiRequest<{ items: readonly TodoItem[] }>('/admin/todo-summary');
    const next = summary.items ?? [];
    announceIncreases(previousByKey, next);
    previousByKey = new Map(next.map((item) => [item.key, item.count]));
    state.items = next;
    state.total = next.reduce((sum, item) => sum + item.count, 0);
    state.loaded = true;
  } catch {
    // 轮询失败静默重试；登出清理由 stopTodoPolling 负责
  }
}

/** 首次加载不提醒；之后任一待办计数增加 → 提示音 + 浏览器系统通知。 */
function announceIncreases(before: ReadonlyMap<string, number>, next: readonly TodoItem[]): void {
  if (before.size === 0 && !state.loaded) return;
  const increased = next.filter((item) => (before.get(item.key) ?? 0) < item.count);
  if (!increased.length) return;
  playChime();
  notifyBrowser(
    `${increased.reduce((sum, item) => sum + item.count, 0)} 笔新待办`,
    increased.map((item) => `${item.label} ${item.count}`).join('、'),
  );
}

let audioCtx: AudioContext | undefined;
function playChime(): void {
  try {
    audioCtx ??= new AudioContext();
    const ctx = audioCtx;
    if (ctx.state === 'suspended') void ctx.resume();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.62);
  } catch {
    // 无声环境（自动播放策略）忽略
  }
}

function notifyBrowser(title: string, body: string): void {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      void Notification.requestPermission();
      return;
    }
    if (Notification.permission !== 'granted') return;
    const notification = new Notification(`聚車缘 · ${title}`, {
      body,
      tag: 'jcy-admin-todo',
    });
    notification.onclick = () => {
      globalThis.focus();
      notification.close();
    };
  } catch {
    // 通知不可用忽略
  }
}

export function startTodoPolling(): void {
  if (timer) return;
  void refresh();
  timer = setInterval(() => void refresh(), POLL_INTERVAL_MS);
}

export function stopTodoPolling(): void {
  if (timer) clearInterval(timer);
  timer = undefined;
  state.items = [];
  state.total = 0;
  state.loaded = false;
  previousByKey = new Map();
}

export function refreshTodos(): Promise<void> {
  return refresh();
}
